import { useNavigate } from '@tanstack/react-router';
import { Building2, Rocket, CheckCircle2, AlertTriangle, ChevronRight } from 'lucide-react';
import { MasterTopbar } from '../shell/MasterTopbar';
import { MasterAuditRow } from '../components/MasterAuditRow';
import { useMasterOverview } from '../hooks/useMasterData';
import { fmtBRL } from '@/lib/utils';

function formatBRLNoCents(cents: number): string {
  return fmtBRL(cents / 100).replace(',00', '');
}

export function MasterGeralScreen() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const { data, isLoading, error } = useMasterOverview();

  if (isLoading) {
    return (
      <div style={{ padding: '0 18px 28px' }}>
        <MasterTopbar title="Visão geral" sub="GestAKI · Master" />
        <SkeletonBlock />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '0 18px 28px' }}>
        <MasterTopbar title="Visão geral" sub="GestAKI · Master" />
        <ErrorCard message={(error as Error | null)?.message ?? 'Não foi possível carregar.'} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
      <MasterTopbar title="Visão geral" sub="GestAKI · Master" />

      {/* Hero MRR — lilás */}
      <div
        style={{
          padding: '18px 20px',
          borderRadius: 18,
          background: 'linear-gradient(155deg, var(--lilac-2) 0%, var(--lilac-deep) 100%)',
          color: '#fff',
          boxShadow: '0 12px 32px -16px rgba(107,91,174,0.6)',
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: 'var(--font-mono)',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          Receita recorrente · MRR
        </div>
        <div
          style={{
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            marginTop: 8,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {formatBRLNoCents(data.mrrCents)}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.82)',
            marginTop: 8,
            fontFamily: 'var(--font-mono)',
          }}
        >
          ARR {formatBRLNoCents(data.mrrCents * 12)} · {data.billing.active} assinaturas ativas
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <KpiTile
          icon={<Building2 size={15} strokeWidth={1.8} />}
          label="Workspaces"
          value={String(data.workspacesTotal)}
          hint={`${data.workspacesActive} ativos`}
        />
        <KpiTile
          icon={<Rocket size={15} strokeWidth={1.8} />}
          label="Onboarding"
          value={String(data.workspacesOnboarding)}
          hint="em trial/pendente"
        />
        <KpiTile
          icon={<CheckCircle2 size={15} strokeWidth={1.8} />}
          label="Prestações"
          value="—"
          hint="próximo release"
          muted
        />
        <KpiTile
          icon={<AlertTriangle size={15} strokeWidth={1.8} />}
          label="Atenção"
          value={String(data.workspacesAttention.length)}
          hint="suspensos/arquivados"
          tone="ink"
        />
      </div>

      {/* Precisa de atenção */}
      {data.workspacesAttention.length > 0 && (
        <div
          style={{
            padding: 14,
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 18,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <strong style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
              Precisa de atenção
            </strong>
            <span
              style={{
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                background: 'var(--warn-soft)',
                color: 'var(--warn)',
                padding: '2px 8px',
                borderRadius: 999,
                fontWeight: 600,
              }}
            >
              {data.workspacesAttention.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.workspacesAttention.map((w) => (
              <button
                key={w.id}
                onClick={() => navigate({ to: '/m/master/workspaces' })}
                style={{
                  textAlign: 'left',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 10px',
                  background: 'var(--card-soft)',
                  border: 0,
                  borderRadius: 12,
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'var(--fg)',
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: 'var(--lilac-deep)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    fontWeight: 600,
                    flex: '0 0 32px',
                  }}
                >
                  {initialsOf(w.name)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {w.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--fg-muted)',
                      marginTop: 1,
                    }}
                  >
                    {w.status === 'SUSPENDED' ? 'suspenso' : 'arquivado'}
                  </div>
                </div>
                <ChevronRight size={15} style={{ color: 'var(--fg-dim)' }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Atividade recente */}
      <div
        style={{
          padding: 14,
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 18,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            marginBottom: 10,
          }}
        >
          Atividade recente
        </div>
        {data.recentAudit.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '8px 0' }}>
            Sem eventos nas últimas horas.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.recentAudit.slice(0, 4).map((a) => (
              <MasterAuditRow
                key={a.id}
                action={a.action}
                createdAt={a.createdAt}
                metadata={a.metadata}
              />
            ))}
          </div>
        )}
        <button
          onClick={() => navigate({ to: '/m/master/auditoria' })}
          style={{
            width: '100%',
            marginTop: 12,
            padding: '10px 12px',
            background: 'var(--card-soft)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 12,
            color: 'var(--fg)',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Ver auditoria completa
        </button>
      </div>
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

interface KpiTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'ink';
  muted?: boolean;
}
function KpiTile({ icon, label, value, hint, tone = 'default', muted }: KpiTileProps) {
  const isInk = tone === 'ink';
  return (
    <div
      style={{
        padding: 12,
        background: isInk ? 'var(--ink-deep)' : 'var(--card)',
        border: isInk ? '1px solid rgba(255,255,255,0.05)' : '1px solid var(--card-stroke)',
        borderRadius: 14,
        color: isInk ? 'var(--on-ink)' : 'var(--fg)',
        opacity: muted ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
          }}
        >
          {label}
        </span>
        <span style={{ color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)' }}>{icon}</span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          marginTop: 6,
          fontFamily: 'var(--font-mono)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function SkeletonBlock() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ height: 90, background: 'var(--card-soft)', borderRadius: 18 }} />
      <div style={{ height: 60, background: 'var(--card-soft)', borderRadius: 14 }} />
      <div style={{ height: 120, background: 'var(--card-soft)', borderRadius: 18 }} />
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: 14,
        background: 'var(--block-soft)',
        color: 'var(--block)',
        borderRadius: 12,
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}
